import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { ListChecks, ClipboardList, ShieldAlert, ShieldCheck, Clock, AlertTriangle, Send, Loader2, TrendingUp, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { StatCard, Card } from "./ui";
import { SeverityBadge } from "../components/SeverityBadge";
import { useToast } from "../hooks/use-toast";

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([api.dashboard(), api.reminders()]);
        setStats(s); setReminders(r);
      } finally { setLoading(false); }
    })();
  }, []);

  const send = async () => {
    setSending(true);
    try { const res = await api.sendReminders(); toast({ title: t("sendReminders"), description: res.message }); }
    finally { setSending(false); }
  };

  if (loading || !stats) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const stageData = Object.entries(stats.statusCounts || {}).map(([name, value]) => ({ name, value }));
  const stageColors = ["#94a3b8", "#60a5fa", "#f59e0b", "#a78bfa", "#ef4444", "#14b8a6", "#10b981", "#64748b"];
  const domainData = Object.entries(stats.domainCounts || {}).map(([name, value]) => ({ name: name.length > 14 ? name.slice(0, 12) + "…" : name, value })).sort((a, b) => b.value - a.value).slice(0, 8);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("dashboard")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{user?.name} · {user?.role}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-teal-50 text-teal-700 border border-teal-200 w-fit">
          <TrendingUp className="w-3.5 h-3.5" /> {stats.complianceScore}% {t("complianceScore")}
        </span>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={ListChecks} label={t("useCases")} value={stats.totalUseCases} tone="teal" />
        <StatCard icon={ClipboardList} label={t("tasks")} value={stats.openTasks} sub={`${stats.totalTasks} ${t("total").toLowerCase()}`} tone="blue" />
        <StatCard icon={ShieldAlert} label={t("openRisks")} value={stats.openRisks} sub={`${stats.criticalRisks} ${t("critical").toLowerCase()}`} tone="amber" />
        <StatCard icon={ShieldCheck} label={t("openIncidents")} value={stats.openIncidents} tone="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <h2 className="font-semibold text-slate-900 mb-4">{t("tasksByStage")}</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stageData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {stageData.map((e, i) => <Cell key={i} fill={stageColors[i % stageColors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-teal-700" /><h2 className="font-semibold text-slate-900">{t("reminders")}</h2></div>
            <button onClick={send} disabled={sending} className="flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50 px-2 py-1 rounded-lg">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {t("sendReminders")}
            </button>
          </div>
          <div className="flex gap-2 mb-3">
            <span className="flex-1 text-center py-2 rounded-lg bg-red-50 text-red-700 text-xs font-semibold">{stats.overdue} {t("overdue")}</span>
            <span className="flex-1 text-center py-2 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold">{stats.dueSoon} {t("dueSoon")}</span>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {reminders.length === 0 && <p className="text-sm text-slate-400 text-center py-6">{t("noItems")}</p>}
            {reminders.slice(0, 12).map((r) => (
              <div key={r.id} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-slate-50">
                {r.reminder === "overdue" ? <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" /> : <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                  <p className="text-[11px] text-slate-400">{r.assignee} · {r.reminder === "overdue" ? `${Math.abs(r.daysDelta)}d overdue` : `${r.daysDelta}d left`}</p>
                </div>
                <SeverityBadge level={r.priority} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="font-semibold text-slate-900 mb-4">{t("useCasesByDomain")}</h2>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={domainData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={110} />
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="#0f766e" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
